'use client';

/* istanbul ignore file */
import { FC, useEffect, useState } from 'react';

import { CalendarCell, CalendarCellProps, Calendar as KendoCalendar } from '@progress/kendo-react-dateinputs';
import { IntlProvider, LocalizationProvider, load, loadMessages } from '@progress/kendo-react-intl';
import '@progress/kendo-theme-fluent/dist/all.css';

import * as currencyData from 'cldr-core/supplemental/currencyData.json';
import * as likelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import * as weekData from 'cldr-core/supplemental/weekData.json';
import * as bg from 'cldr-dates-full/main/bg/ca-gregorian.json';
import * as cs from 'cldr-dates-full/main/cs/ca-gregorian.json';
import * as da from 'cldr-dates-full/main/da/ca-gregorian.json';
import * as de from 'cldr-dates-full/main/de/ca-gregorian.json';
import * as el from 'cldr-dates-full/main/el/ca-gregorian.json';
import * as enGB from 'cldr-dates-full/main/en-GB/ca-gregorian.json';
import * as es from 'cldr-dates-full/main/es/ca-gregorian.json';
import * as fi from 'cldr-dates-full/main/fi/ca-gregorian.json';
import * as fr from 'cldr-dates-full/main/fr/ca-gregorian.json';
import * as hr from 'cldr-dates-full/main/hr/ca-gregorian.json';
import * as hu from 'cldr-dates-full/main/hu/ca-gregorian.json';
import * as it from 'cldr-dates-full/main/it/ca-gregorian.json';
import * as lt from 'cldr-dates-full/main/lt/ca-gregorian.json';
import * as mk from 'cldr-dates-full/main/mk/ca-gregorian.json';
import * as nl from 'cldr-dates-full/main/nl/ca-gregorian.json';
import * as nn from 'cldr-dates-full/main/nn/ca-gregorian.json';
import * as pl from 'cldr-dates-full/main/pl/ca-gregorian.json';
import * as pt from 'cldr-dates-full/main/pt/ca-gregorian.json';
import * as ro from 'cldr-dates-full/main/ro/ca-gregorian.json';
import * as ru from 'cldr-dates-full/main/ru/ca-gregorian.json';
import * as sk from 'cldr-dates-full/main/sk/ca-gregorian.json';
import * as sl from 'cldr-dates-full/main/sl/ca-gregorian.json';
import * as sr from 'cldr-dates-full/main/sr/ca-gregorian.json';
import * as sv from 'cldr-dates-full/main/sv/ca-gregorian.json';
import * as tr from 'cldr-dates-full/main/tr/ca-gregorian.json';
import * as uk from 'cldr-dates-full/main/uk/ca-gregorian.json';

import './Calendar.css';
import * as translations from './Calendar.json';

/* istanbul ignore next */

load(likelySubtags, currencyData, weekData, bg, cs, da, de, el, enGB, es, fi, fr, hr, hu, it, lt, mk, nl, nn, pl, pt, ro, ru, sk, sl, sr, sv, tr, uk);

Object.keys(translations).forEach((locale) => loadMessages((translations as any)[locale], locale));

type Day = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';

interface Props {
   value?: Date;
   min?: Date;
   max?: Date;
   disabledDates?: Array<Date>;
   disabledDays?: Array<Day>;
   onChange?: (date: Date) => void;
   locale?: string;
}

export const Calendar: FC<Props> = ({ value, min = new Date(1900, 0, 1), max = new Date(2099, 11, 31), disabledDates = [], disabledDays = [], onChange, locale = 'en-GB' }) => {
   const [selectedDate, setSelectedDate] = useState<Date | undefined>(value);

   const checkDisabledDate = (date: Date) => {
      if (date < min || date > max) return true;

      const isDisabledDay = disabledDays.includes(date.toLocaleString('en-GB', { weekday: 'long' }) as Day);
      const isDisabledDate = disabledDates.some((disabledDate) => disabledDate.getTime() === date.getTime());

      return isDisabledDay || isDisabledDate;
   };

   const handleChange = (newDate: Date) => {
      if (checkDisabledDate(newDate)) return;
      setSelectedDate(newDate);
      onChange?.(newDate);
   };

   useEffect(() => setSelectedDate(value), [value]);

   // eslint-disable-next-line react/no-unstable-nested-components
   const Cell = (props: CalendarCellProps) => {
      const { value: date } = props;
      const isDisabled = checkDisabledDate(date);

      return (
         <div className={`table-cell ${isDisabled ? 'pointer-events-none opacity-60' : ''}`}>
            <CalendarCell {...props} />
         </div>
      );
   };

   return (
      <LocalizationProvider language={locale.split('-')[0]}>
         <IntlProvider locale={locale}>
            <KendoCalendar cell={Cell} smoothScroll topView="month" min={min} max={max} value={selectedDate} onChange={(e) => handleChange(e.value)} />
         </IntlProvider>
      </LocalizationProvider>
   );
};
